// frontend/src/components/HelpModal.js
// Componente para mostrar una ventana modal con la guía de uso de la aplicación.
// VERSIÓN FINAL 2.0: Guía de usuario extendida y detallada.

import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';

/**
 * @component HelpModal
 * @description Muestra un modal con una guía de usuario detallada y exhaustiva.
 * @param {object} props - Propiedades del componente.
 * @param {boolean} props.show - Si el modal debe mostrarse.
 * @param {function} props.onClose - Callback para cerrar el modal.
 */
function HelpModal({ show, onClose }) {
  if (!show) {
    return null;
  }

  // Componentes de estilo para la guía
  const Section = ({ title, children }) => (
    <section className="py-4">
      <h3 className="text-xl font-bold text-gray-900 mb-3 border-l-4 border-blue-500 pl-3">{title}</h3>
      <div className="text-gray-700 space-y-3">{children}</div>
    </section>
  );

  const SubSection = ({ title, children }) => (
    <div className="mt-4 pl-4">
        <h4 className="text-lg font-semibold text-gray-800 mb-2">{title}</h4>
        <div className="text-sm text-gray-600 space-y-2 pl-3 border-l-2 border-gray-200">{children}</div>
    </div>
  );

  const Tip = ({ children }) => (
    <div className="p-3 mt-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
        <strong>Consejo:</strong> {children}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-4 border-b pb-3">
          <h2 className="text-2xl font-extrabold text-gray-900">Manual de Usuario de la Plataforma</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-800 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="overflow-y-auto pr-3 space-y-4">
          <Section title="Bienvenido al Centro de Control">
            <p>
              Esta plataforma está diseñada para centralizar y estandarizar la gestión de tus iniciativas con clientes. Desde el <strong>Centro de Control</strong> (la pantalla de inicio), tienes acceso a todos los módulos.
            </p>
          </Section>

          <Section title="Módulos para Todos los Usuarios">
            <SubSection title="Dashboard de Iniciativas">
                <p>Es la vista principal para la gestión diaria. Te permite visualizar y filtrar todas las iniciativas en curso o finalizadas.</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                    <li><strong>Pestañas de Filtro Rápido:</strong> Cambia rápidamente entre tipos de iniciativa (MAP, PoC, etc.) para acotar tu búsqueda.</li>
                    <li><strong>Filtros Adicionales:</strong> Usa los desplegables y campos de fecha para encontrar iniciativas por estado, responsable, número de oportunidad y más.</li>
                    <li><strong>Creación Rápida:</strong> Desde aquí, puedes usar el botón "Añadir Iniciativa" para empezar un nuevo proyecto.</li>
                    <li><strong>Acciones por Iniciativa:</strong> En la tabla, cada fila tiene iconos para:
                        <ul className="list-['-_'] list-inside ml-6">
                            <li><EyeIcon className="h-4 w-4 inline-block mr-1"/><strong>Ver Detalles:</strong> Entra a la vista completa de la iniciativa.</li>
                            <li><PencilIcon className="h-4 w-4 inline-block mr-1"/><strong>Editar:</strong> Modifica los metadatos generales de la iniciativa.</li>
                            <li><TrashIcon className="h-4 w-4 inline-block mr-1"/><strong>Eliminar:</strong> Borra la iniciativa y todos sus datos asociados.</li>
                        </ul>
                    </li>
                </ul>
            </SubSection>
            
            <SubSection title="Detalles de una Iniciativa">
                <p>Esta es la vista de trabajo para una iniciativa específica. Aquí gestionas su progreso y documentación.</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                    <li><strong>Marcar Actividades:</strong> Simplemente haz clic en el checkbox junto a una tarea para marcarla como completada. El progreso de la fase y el progreso general se recalcularán automáticamente.</li>
                    <li><strong>Subir Archivos:</strong> En la sección de "Documentos y Comentarios", navega a la pestaña de la fase correcta y haz clic en "Subir Archivo" para adjuntar el documento requerido.</li>
                    <li><strong>Sincronizar:</strong> Si un administrador ha cambiado la plantilla base (por ejemplo, ha añadido una nueva tarea), el botón <strong>"Sincronizar"</strong> actualizará tu iniciativa para reflejar esos cambios sin perder tu progreso en las tareas existentes.</li>
                </ul>
            </SubSection>

            <SubSection title="Gestión de Clientes">
                <p>Aquí administras el catálogo de clientes. Es un paso fundamental, ya que cada iniciativa debe estar asociada a un cliente existente.</p>
            </SubSection>
          </Section>

          <Section title="Módulos de Administración (Solo Admins)">
            <p>
              Estas secciones avanzadas te dan control total sobre la configuración y los datos maestros de la plataforma.
            </p>
            <SubSection title="Gestión de Plantillas">
                <p>Aquí defines los "planos" para cada tipo de iniciativa. Los cambios que hagas aquí se aplicarán a las <strong>nuevas</strong> iniciativas que se creen, y los proyectos existentes podrán actualizarse mediante el botón "Sincronizar".</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                    <li><strong>Añadir/Editar Fases:</strong> Puedes crear nuevas etapas para un tipo de iniciativa o editar las existentes. El campo "Orden" determina la posición vertical en la que aparecerá la fase.</li>
                    <li><strong>Añadir/Editar Tareas:</strong> Dentro de cada fase, puedes añadir, editar o eliminar las tareas estándar. El campo "Orden" es crucial para definir la secuencia de las tareas.</li>
                    <li><strong>Añadir/Editar Documentos:</strong> Define qué archivos son requeridos. Puedes asignarlos a una fase específica o dejarlos como "General". El campo "Clave Interna" se genera automáticamente y no debe ser modificado.</li>
                </ul>
                <Tip>Para reordenar fases, tareas o documentos, simplemente edita el elemento y cambia su número en el campo "Orden". Un número más bajo aparecerá más arriba.</Tip>
            </SubSection>

            <SubSection title="Gestión de Usuarios">
                <p>Administra quién tiene acceso a la plataforma. Puedes crear nuevos usuarios y asignarles uno de dos roles:</p>
                <ul className="list-disc list-inside ml-4">
                    <li><strong>Usuario:</strong> Puede gestionar clientes e iniciativas, pero no puede acceder a los Módulos de Administración.</li>
                    <li><strong>Administrador:</strong> Tiene acceso total a todas las funciones, incluyendo la configuración de plantillas y backups.</li>
                </ul>
            </SubSection>

            <SubSection title="Gestión de Backups">
                <p>Esta es una herramienta crítica para la seguridad y portabilidad de los datos.</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                    <li><strong>Descargar Backup:</strong> Crea un archivo <code>.zip</code> con una copia completa y segura de toda la base de datos (incluyendo usuarios con sus contraseñas encriptadas, clientes, proyectos, plantillas) y todos los archivos físicos que se hayan subido.</li>
                    <li><strong>Restaurar desde Archivo:</strong> Es una operación <strong>destructiva y total</strong>. Reemplaza el estado actual de la aplicación con el contenido exacto del backup. Es ideal para migraciones de servidor o recuperación ante desastres. Se creará un backup automático del estado actual antes de proceder.</li>
                </ul>
            </SubSection>
          </Section>
        </div>

        <div className="mt-6 pt-4 border-t text-right">
            <button
                onClick={onClose}
                className="px-6 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
                Entendido
            </button>
        </div>
      </div>
    </div>
  );
}

// Componentes inline para iconos, para evitar errores si no se importan.
const EyeIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" {...props}>
        <path d="M10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
        <path fillRule="evenodd" d="M.664 10.59a1.651 1.651 0 010-1.18l.88-1.473a1.65 1.65 0 011.53-.895h13.852a1.65 1.65 0 011.53.895l.88 1.473a1.651 1.651 0 010 1.18l-.88 1.473a1.65 1.65 0 01-1.53.895H3.074a1.65 1.65 0 01-1.53-.895L.664 10.59zM10 15a5 5 0 100-10 5 5 0 000 10z" clipRule="evenodd" />
    </svg>
);
const PencilIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" {...props}>
        <path d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z" />
    </svg>
);
const TrashIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" {...props}>
        <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.58.22-2.365.468a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25V4c.827-.05 1.66-.075 2.5-.075zM8.47 16.25a.75.75 0 01.75-.75h2.5a.75.75 0 010 1.5h-2.5a.75.75 0 01-.75-.75z" clipRule="evenodd" />
    </svg>
);

export default HelpModal;
